import api from '../../lib/api';
import { DueDateCell } from '../../utils/dueDate';
import { invoiceAmounts } from '../../utils/invoiceAmounts';
import { formatRupiah } from '../../utils/printHelper';
import { Pagination } from '../Pagination';
import StatusBadge from '../StatusBadge';
import { Button } from '../ui/button';
import { CardContent } from '../ui/card';
import { Input } from '../ui/input';
import { id } from 'date-fns/locale';
import { CheckCircle, Eye, Pencil, Printer, Send, Trash2, Undo2 } from 'lucide-react';
import { toast } from 'sonner';

export function InvoiceTable({ SortIcon, canFixReturned, currentPage, fetchInvoices, getUmurBadge, getUmurHutang, handleDeleteReturned, handlePrint, handlePrintBuktiTukar, handleRevertInvoice, handleSort, handleSubmit, invoices, openDeleteDialog, openEdit, openSiapProses, openTukarFaktur, setFixTarget, setReturnTarget, setSelectedInvoice, setShowDetail, setShowVoucherDialog, setVoucherInvoice, setVoucherKeterangan, sortField, totalCount, totalPages, user }) {
  return (
    <CardContent>
      {invoices.length > 0 ? (
        <>
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="bg-slate-50 text-left">
                <th className="px-4 py-3 text-sm font-semibold">Invoice Number</th>
                <th 
                  className="px-4 py-3 text-sm font-semibold cursor-pointer transition-colors"
                  style={sortField === 'invoice_date' ? { backgroundColor: '#ffee8b' } : {}}
                  onClick={() => handleSort('invoice_date')}
                >
                  <div className="flex items-center gap-1">
                    Tanggal <SortIcon field="invoice_date" />
                  </div>
                </th>
                <th 
                  className="px-4 py-3 text-sm font-semibold cursor-pointer transition-colors"
                  style={sortField === 'supplier_name' ? { backgroundColor: '#ffee8b' } : {}}
                  onClick={() => handleSort('supplier_name')}
                >
                  <div className="flex items-center gap-1">
                    Supplier <SortIcon field="supplier_name" />
                  </div>
                </th>
                <th 
                  className="px-4 py-3 text-sm font-semibold cursor-pointer transition-colors"
                  style={sortField === 'company_name' ? { backgroundColor: '#ffee8b' } : {}}
                  onClick={() => handleSort('company_name')}
                >
                  <div className="flex items-center gap-1">
                    Perusahaan <SortIcon field="company_name" />
                  </div>
                </th>
                <th className="px-4 py-3 text-sm font-semibold">Nilai Dibayarkan</th>
                <th 
                  className="px-4 py-3 text-sm font-semibold cursor-pointer transition-colors"
                  style={sortField === 'status' ? { backgroundColor: '#ffee8b' } : {}}
                  onClick={() => handleSort('status')}
                >
                  <div className="flex items-center gap-1">
                    Status <SortIcon field="status" />
                  </div>
                </th>
                <th className="px-4 py-3 text-sm font-semibold">Jatuh Tempo</th>
                <th className="px-4 py-3 text-sm font-semibold">Umur Hutang</th>
                <th className="px-4 py-3 text-sm font-semibold">Submit dari PO</th>
                <th className="px-4 py-3 text-sm font-semibold">Monitoring Berkas</th>
                <th className="px-4 py-3 text-sm font-semibold">Actions</th>
              </tr>
            </thead>
            <tbody>
              {invoices.map((invoice) => {
                const umurHari = getUmurHutang(invoice.invoice_date);
                const umurBadge = getUmurBadge(umurHari);
                return (
                <tr key={invoice.id} className="border-b" data-testid={`invoice-row-${invoice.id}`}>
                  <td className="px-4 py-3 font-mono text-sm font-semibold">{invoice.invoice_number}</td>
                  <td className="px-4 py-3 text-sm" style={sortField === 'invoice_date' ? { backgroundColor: '#ffee8b33' } : {}}>{new Date(invoice.invoice_date).toLocaleDateString('id-ID')}</td>
                  <td className="px-4 py-3" style={sortField === 'supplier_name' ? { backgroundColor: '#ffee8b33' } : {}}>{invoice.supplier_name}</td>
                  <td className="px-4 py-3" style={sortField === 'company_name' ? { backgroundColor: '#ffee8b33' } : {}}>{invoice.company_name || '-'}</td>
                  <td className="px-4 py-3 font-mono">Rp {formatRupiah(invoiceAmounts(invoice).nilaiDibayarkan)}</td>
                  <td className="px-4 py-3" style={sortField === 'status' ? { backgroundColor: '#ffee8b33' } : {}}>
                    <div className="flex items-center gap-1.5">
                      <StatusBadge status={invoice.status} />
                      {invoice.uses_va && <span className="text-xs bg-amber-100 text-amber-700 px-1.5 py-0.5 rounded font-semibold" title="Virtual Account">VA</span>}
                      {invoice.dp_amount > 0 && <span className="text-xs bg-orange-100 text-orange-700 px-1.5 py-0.5 rounded font-semibold" title={`DP: Rp ${(invoice.dp_amount || 0).toLocaleString('id-ID')}`}>DP</span>}
                    </div>
                    {invoice.status === 'returned' && invoice.return_reason && (
                      <p className="text-xs text-orange-800 mt-1 max-w-[220px]" data-testid={`return-reason-${invoice.id}`} title={invoice.return_reason}>
                        Alasan: {invoice.return_reason}
                      </p>
                    )}
                    {invoice.status !== 'returned' && invoice.return_count > 0 && (
                      <p className="text-[10px] text-slate-500 mt-1" data-testid={`resubmitted-${invoice.id}`}>Pernah dikembalikan {invoice.return_count}x</p>
                    )}
                    {invoice.paid_at && (
                      <p className="text-xs text-slate-500 mt-1" data-testid={`invoice-paid-at-${invoice.id}`}>
                        Dibayar {new Date(invoice.paid_at).toLocaleDateString('id-ID')}
                      </p>
                    )}
                  </td>
                  <td className="px-4 py-3"><DueDateCell invoice={invoice} settled={['terbayar', 'bank_process', 'returned'].includes(invoice.status)} /></td>
                  <td className="px-4 py-3">
                    <span className={`px-2 py-1 rounded text-xs ${umurBadge.color}`} title={umurBadge.status}>
                      {umurBadge.label}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-xs">
                    {invoice.submitted_from_po_at ? (
                      <div className="space-y-0.5">
                        <div className="font-mono">{new Date(invoice.submitted_from_po_at).toLocaleDateString('id-ID')}</div>
                        <div className="text-slate-500">{new Date(invoice.submitted_from_po_at).toLocaleTimeString('id-ID', { hour: '2-digit', minute: '2-digit' })}</div>
                        <div className="text-slate-400 truncate max-w-[100px]" title={invoice.submitted_from_po_by_name}>{invoice.submitted_from_po_by_name}</div>
                      </div>
                    ) : (
                      <span className="text-slate-400">-</span>
                    )}
                  </td>
                  <td className="px-4 py-3">
                    <div className="space-y-1 text-xs">
                      {/* Gudang: checkbox to send document */}
                      {user?.role === 'staff_clerk' && !invoice.dokumen_dikirim && (
                        <Button
                          size="sm"
                          className="bg-blue-500 hover:bg-blue-600 text-white text-xs h-7"
                          onClick={async () => {
                            try {
                              await api.patch(`/invoices-full/${invoice.id}/dokumen-dikirim`);
                              toast.success('Dokumen ditandai sudah dikirim ke AP');
                              fetchInvoices();
                            } catch (e) { toast.error(e.response?.data?.detail || 'Gagal'); }
                          }}
                          data-testid={`kirim-berkas-${invoice.id}`}
                        >
                          Kirim Berkas
                        </Button>
                      )}
                      {invoice.dokumen_dikirim && (
                        <div className="flex items-center gap-1">
                          <span className="inline-block w-2 h-2 rounded-full bg-blue-500"></span>
                          <span className="text-blue-700">Dikirim</span>
                        </div>
                      )}
                      {/* AP: checkbox to receive document */}
                      {user?.role === 'account_payable' && invoice.dokumen_dikirim && !invoice.dokumen_diterima && (
                        <Button
                          size="sm"
                          className="bg-green-500 hover:bg-green-600 text-white text-xs h-7"
                          onClick={async () => {
                            try {
                              await api.patch(`/invoices-full/${invoice.id}/dokumen-diterima`);
                              toast.success('Dokumen ditandai sudah diterima');
                              fetchInvoices();
                            } catch (e) { toast.error(e.response?.data?.detail || 'Gagal'); }
                          }}
                          data-testid={`terima-berkas-${invoice.id}`}
                        >
                          Terima Berkas
                        </Button>
                      )}
                      {invoice.dokumen_diterima && (
                        <div className="flex items-center gap-1">
                          <span className="inline-block w-2 h-2 rounded-full bg-green-500"></span>
                          <span className="text-green-700">Diterima</span>
                        </div>
                      )}
                      {!invoice.dokumen_dikirim && user?.role !== 'staff_clerk' && (
                        <span className="text-slate-400">Belum dikirim</span>
                      )}
                      {/* Admin sees both */}
                      {user?.role === 'admin' && !invoice.dokumen_dikirim && (
                        <Button
                          size="sm"
                          className="bg-blue-500 hover:bg-blue-600 text-white text-xs h-7"
                          onClick={async () => {
                            try {
                              await api.patch(`/invoices-full/${invoice.id}/dokumen-dikirim`);
                              toast.success('Dokumen ditandai sudah dikirim');
                              fetchInvoices();
                            } catch (e) { toast.error(e.response?.data?.detail || 'Gagal'); }
                          }}
                        >
                          Kirim Berkas
                        </Button>
                      )}
                      {user?.role === 'admin' && invoice.dokumen_dikirim && !invoice.dokumen_diterima && (
                        <Button
                          size="sm"
                          className="bg-green-500 hover:bg-green-600 text-white text-xs h-7"
                          onClick={async () => {
                            try {
                              await api.patch(`/invoices-full/${invoice.id}/dokumen-diterima`);
                              toast.success('Dokumen ditandai sudah diterima');
                              fetchInvoices();
                            } catch (e) { toast.error(e.response?.data?.detail || 'Gagal'); }
                          }}
                        >
                          Terima Berkas
                        </Button>
                      )}
                    </div>
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex gap-2 flex-wrap">
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => { setSelectedInvoice(invoice); setShowDetail(true); }}
                        data-testid={`view-invoice-${invoice.id}`}
                        title="Lihat Detail"
                      >
                        <Eye className="h-4 w-4" />
                      </Button>
                      {['account_payable', 'admin'].includes(user?.role) && (
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => handlePrint(invoice)}
                          data-testid={`print-invoice-${invoice.id}`}
                          title="Print PDF"
                        >
                          <Printer className="h-4 w-4" />
                        </Button>
                      )}
                      {/* Print Voucher Pembayaran for AP and Approval */}
                      {['account_payable', 'approval', 'admin'].includes(user?.role) && (
                        <Button
                          size="sm"
                          variant="outline"
                          className="border-purple-400 text-purple-700 hover:bg-purple-50"
                          onClick={() => { setVoucherInvoice(invoice); setVoucherKeterangan(''); setShowVoucherDialog(true); }}
                          data-testid={`print-voucher-${invoice.id}`}
                          title="Print Voucher Pembayaran"
                        >
                          <Printer className="h-4 w-4" />
                        </Button>
                      )}
                      {invoice.status === 'draft' && ['purchasing', 'staff_clerk', 'admin'].includes(user?.role) && (
                        <>
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => openEdit(invoice)}
                            data-testid={`edit-invoice-${invoice.id}`}
                            title="Edit Invoice"
                          >
                            <Pencil className="h-4 w-4" />
                          </Button>
                          <Button
                            size="sm"
                            className="bg-indigo-600 hover:bg-indigo-700"
                            onClick={() => handleSubmit(invoice.id)}
                            data-testid={`submit-invoice-${invoice.id}`}
                            title="Submit ke AP"
                          >
                            <Send className="h-4 w-4 mr-1" />
                            <span className="text-xs">Submit AP</span>
                          </Button>
                        </>
                      )}
                      {/* Siap Proses button for AP users */}
                      {invoice.status === 'submitted' && ['account_payable', 'admin'].includes(user?.role) && (
                        <>
                          <Button
                            size="sm"
                            className={`text-white ${invoice.dokumen_diterima ? 'bg-emerald-600 hover:bg-emerald-700' : 'bg-slate-400 cursor-not-allowed'}`}
                            onClick={() => invoice.dokumen_diterima ? openSiapProses(invoice) : toast.error('Dokumen belum diterima. Pastikan Gudang sudah mengirim berkas dan AP sudah mengkonfirmasi penerimaan.')}
                            data-testid={`siap-proses-${invoice.id}`}
                            title={invoice.dokumen_diterima ? 'Siap Proses - Input PPh & Admin Bank' : 'Dokumen belum diterima'}
                          >
                            <CheckCircle className="h-4 w-4 mr-1" />
                            <span className="text-xs">Siap Proses</span>
                          </Button>
                          <Button
                            size="sm"
                            className="bg-amber-600 hover:bg-amber-700 text-white"
                            onClick={() => openTukarFaktur(invoice)}
                            data-testid={`tukar-faktur-${invoice.id}`}
                            title="Tukar Faktur"
                          >
                            <span className="text-xs">Tukar Faktur</span>
                          </Button>
                        </>
                      )}
                      {/* Print Bukti Tukar for exchanged invoices */}
                      {invoice.tukar_faktur && (
                        <Button
                          size="sm"
                          className="bg-green-600 hover:bg-green-700 text-white"
                          onClick={() => handlePrintBuktiTukar(invoice.id)}
                          data-testid={`print-bukti-tukar-${invoice.id}`}
                          title="Print Bukti Tukar Faktur"
                        >
                          <Printer className="h-4 w-4 mr-1" />
                          <span className="text-xs">Bukti Tukar</span>
                        </Button>
                      )}
                      {/* Revert button for proses_bayar invoices */}
                      {['account_payable', 'approval', 'admin'].includes(user?.role) && ['proses_bayar'].includes(invoice.status) && (
                        <Button
                          size="sm"
                          variant="outline"
                          className="border-amber-400 text-amber-700 hover:bg-amber-50"
                          onClick={() => handleRevertInvoice(invoice.id)}
                          data-testid={`revert-invoice-${invoice.id}`}
                          title="Kembalikan ke Submitted"
                        >
                          <Undo2 className="h-4 w-4" />
                        </Button>
                      )}
                      {/* AP: kembalikan invoice Submitted ke clerk */}
                      {['account_payable', 'admin'].includes(user?.role) && invoice.status === 'submitted' && (
                        <Button
                          size="sm"
                          variant="outline"
                          className="border-orange-400 text-orange-700 hover:bg-orange-50"
                          onClick={() => setReturnTarget(invoice)}
                          data-testid={`return-invoice-${invoice.id}`}
                          title="Kembalikan ke Clerk untuk diperbaiki"
                        >
                          <Undo2 className="h-4 w-4 mr-1" />
                          <span className="text-xs">Kembalikan</span>
                        </Button>
                      )}
                      {/* Clerk: perbaiki / hapus invoice yang dikembalikan */}
                      {invoice.status === 'returned' && canFixReturned(invoice) && (
                        <>
                          <Button
                            size="sm"
                            className="bg-indigo-600 hover:bg-indigo-700 text-white"
                            onClick={() => setFixTarget(invoice)}
                            data-testid={`fix-returned-${invoice.id}`}
                            title="Perbaiki & Submit Ulang"
                          >
                            <Pencil className="h-4 w-4 mr-1" />
                            <span className="text-xs">Perbaiki</span>
                          </Button>
                          <Button
                            size="sm"
                            variant="outline"
                            className="border-red-400 text-red-700 hover:bg-red-50"
                            onClick={() => handleDeleteReturned(invoice)}
                            data-testid={`delete-returned-${invoice.id}`}
                            title="Hapus invoice yang dikembalikan"
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </>
                      )}
                      {/* Admin Delete Button */}
                      {user?.role === 'admin' && (
                        <Button
                          size="sm"
                          variant="destructive"
                          onClick={() => openDeleteDialog(invoice)}
                          data-testid={`delete-invoice-${invoice.id}`}
                          title="Hapus Invoice"
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      )}
                    </div>
                  </td>
                </tr>
              )})}
            </tbody>
          </table>
        </div>
        <Pagination page={currentPage} totalPages={totalPages} total={totalCount} onPageChange={(p) => fetchInvoices(p)} />
        </>
      ) : (
        <p className="text-slate-500 text-center py-8">Belum ada invoice</p>
      )}
    </CardContent>
  );
}
